import React, { useEffect, useRef, useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { api, ApiError, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { cn } from "@/lib/utils";

export type InlineKind = "text" | "number" | "date" | "datetime" | "enum" | "phone" | "email";

/** `2026-11-02T09:30:00.000Z` → the local `2026-11-02T15:00` a date-time input takes */
const toLocalInput = (iso: unknown) => {
  const d = iso ? new Date(String(iso)) : null;
  if (!d || isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const initialText = (kind: InlineKind, value: unknown) => {
  if (value === null || value === undefined) return "";
  if (kind === "date") return String(value).slice(0, 10);
  if (kind === "datetime") return toLocalInput(value);
  return String(value);
};

/**
 * One cell of a list that can be changed in place. It shows the cell as it is; a pencil appears on hover (or double-click) and
 * opens the editor: Enter or leaving the box saves, Esc cancels, a choice saves at once. The change goes through
 * PATCH InlineEdit/<list>/<id> with the value that was shown, so a change someone else made first is refused (the list is
 * refreshed) instead of overwritten. `onSaved` gets the refreshed fields of the row.
 */
export function InlineCell({
  list,
  id,
  field,
  kind,
  value,
  display,
  enabled = true,
  options,
  placeholder,
  onSaved,
  onConflict,
  className,
}: {
  list: string;
  id: string;
  field: string;
  kind: InlineKind;
  /** The value as the list holds it (sent back as `previous`) */
  value: unknown;
  display: React.ReactNode;
  /** false: the person may not edit it, or the record is locked (closed deal, converted lead…) */
  enabled?: boolean;
  options?: { value: string; label: string }[];
  placeholder?: string;
  onSaved: (row: Record<string, unknown>) => void;
  onConflict?: () => void;
  className?: string;
}) {
  const { error } = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);
  const done = useRef(false);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  if (!enabled) return <>{display}</>;

  const start = (e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    done.current = false;
    setDraft(initialText(kind, value));
    setEditing(true);
  };

  const cancel = () => {
    done.current = true;
    setEditing(false);
  };

  const save = async (text: string) => {
    if (done.current) return;
    done.current = true;
    if (text === initialText(kind, value)) {
      setEditing(false);
      return;
    }
    // A date-time box holds local time; the server takes a moment in time
    const payload = kind === "datetime" && text ? new Date(text).toISOString() : text;
    setSaving(true);
    try {
      const res = await api.patch(`/api/InlineEdit/${list}/${id}`, { field, value: payload, previous: value ?? null });
      const row = unwrapObject<{ row?: Record<string, unknown> }>(res).row;
      if (row) onSaved(row);
      setEditing(false);
    } catch (err: any) {
      const conflict = err instanceof ApiError && err.status === 409;
      error(conflict ? "Someone changed this first" : "Couldn't save the change", err?.message || "Something went wrong");
      setEditing(false);
      if (conflict) onConflict?.();
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    const common = {
      ref: inputRef as any,
      value: draft,
      disabled: saving,
      onClick: (e: React.MouseEvent) => e.stopPropagation(),
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setDraft(e.target.value);
        // A choice from a list is final at once
        if (kind === "enum") void save(e.target.value);
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        e.stopPropagation();
        if (e.key === "Enter") {
          e.preventDefault();
          void save(draft);
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancel();
        }
      },
      onBlur: () => void save(draft),
      className: cn("h-7 w-full min-w-[110px] rounded-md border border-primary/50 bg-background px-2 text-xs outline-none ring-2 ring-primary/20", saving && "opacity-60"),
    };
    return (
      <div className={cn("flex items-center gap-1", className)} onClick={(e) => e.stopPropagation()}>
        {kind === "enum" ? (
          <select {...(common as any)}>
            {(options || []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            {...(common as any)}
            type={kind === "number" ? "number" : kind === "date" ? "date" : kind === "datetime" ? "datetime-local" : kind === "email" ? "email" : "text"}
            step={kind === "number" ? "any" : undefined}
            min={kind === "number" ? 0 : undefined}
            placeholder={placeholder}
          />
        )}
        {saving && <Loader2 className="size-3.5 shrink-0 animate-spin text-muted-foreground" />}
      </div>
    );
  }

  return (
    <div className={cn("group/inline relative inline-flex max-w-full items-center", className)} onDoubleClick={start}>
      {display}
      <button
        type="button"
        onClick={start}
        aria-label={`Edit ${field.replace(/_/g, " ")}`}
        title="Edit here (or double-click)"
        className="ml-1 flex size-5 shrink-0 cursor-pointer items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/inline:opacity-100"
      >
        <Pencil className="size-3" />
      </button>
    </div>
  );
}
